import Modal from "react-modal";
import React from "react";
import toast from "react-hot-toast";
import { Oval } from "react-loader-spinner";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  setShowUpdatePaymentTypeModal,
  updatePaymentType,
  resetUpdatePaymentType,
} from "../ordersSlice";
import { ReactComponent as Close } from "../../../assets/images/close.svg";
import { PaymentType } from "../../../models";

import "./UpdatePaymentTypeModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

export interface UpdatePaymentTypeFormModel {
  paymentType: PaymentType;
}

const validationSchema = Yup.object().shape({
  paymentType: Yup.string().required("Payment type is required"),
});

const paymentTypeOptions: { value: PaymentType; label: string }[] = [
  { value: PaymentType.CASH, label: "Cash" },
  { value: PaymentType.CARD, label: "Card" },
  { value: PaymentType.CHECK, label: "Check" },
  { value: PaymentType.PAYPAL, label: "PayPal" },
  { value: PaymentType.VENMO, label: "Venmo" },
  { value: PaymentType.OWES, label: "Owes" },
  { value: PaymentType.ACH, label: "ACH" },
  { value: PaymentType.CASH_APP, label: "Cash App" },
  { value: PaymentType.ZELLE, label: "Zelle" },
  { value: PaymentType.APPLE_CASH, label: "Apple Cash" },
];

export const UpdatePaymentTypeModal = () => {
  const dispatch = useAppDispatch();

  const showUpdatePaymentTypeModal = useAppSelector(
    (state) => state.orders.showUpdatePaymentTypeModal
  );
  const updatePaymentTypeAPI = useAppSelector(
    (state) => state.orders.updatePaymentTypeAPI
  );

  const isOpen = !!showUpdatePaymentTypeModal;

  React.useEffect(() => {
    if (!updatePaymentTypeAPI.loading && !!updatePaymentTypeAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !updatePaymentTypeAPI.loading &&
      !updatePaymentTypeAPI.error &&
      updatePaymentTypeAPI.value
    ) {
      toast.success("Successfully updated payment type.");
      dispatch(resetUpdatePaymentType());
      dispatch(setShowUpdatePaymentTypeModal(undefined));
    }
  }, [
    updatePaymentTypeAPI.loading,
    updatePaymentTypeAPI.error,
    updatePaymentTypeAPI.value,
    dispatch,
  ]);

  const onClose = () => {
    dispatch(setShowUpdatePaymentTypeModal(undefined));
  };

  const onSubmit = (form: UpdatePaymentTypeFormModel) => {
    if (!showUpdatePaymentTypeModal) return;

    dispatch(
      updatePaymentType({
        id: showUpdatePaymentTypeModal.id,
        paymentType: form.paymentType,
      })
    );
  };

  const initialValues: UpdatePaymentTypeFormModel = {
    paymentType: showUpdatePaymentTypeModal?.paymentType || PaymentType.CASH,
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles}>
      <div className="update-payment-type-modal-container">
        <Close className="close-button" onClick={onClose} />

        <div className="title">Update Payment Type</div>

        {showUpdatePaymentTypeModal && (
          <div className="order-address">{showUpdatePaymentTypeModal.address}</div>
        )}

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ errors, touched }) => (
            <Form className="update-payment-type-form-component">
              <div className="field-container">
                <div className="label">Payment Type:</div>
                <div className="radio-group">
                  {paymentTypeOptions.map((option) => (
                    <label key={option.value} className="radio-option">
                      <Field
                        type="radio"
                        name="paymentType"
                        value={option.value}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.paymentType && touched.paymentType && (
                  <div className="form-error">{errors.paymentType}</div>
                )}
              </div>

              <div className="buttons-container">
                <button
                  className="button secondary-button"
                  disabled={updatePaymentTypeAPI.loading}
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button"
                  disabled={updatePaymentTypeAPI.loading}
                >
                  Update
                  {updatePaymentTypeAPI.loading && (
                    <div style={{ marginLeft: 8 }}>
                      <Oval
                        color="#131313"
                        height={20}
                        width={20}
                        secondaryColor="#FBD20A"
                      />
                    </div>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};
